import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SkeletonText,
    Text,
    Tooltip,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useGetPixelsByAdaccountQuery } from 'store/BmAccounts/bmAccountApi';
import { useGetCustomConversionEventsQuery, useGetCustomStatsQuery } from 'store/campaigns/adsetFormApi';
import { useCreatePixelMutation } from 'store/facebookServices/facebookServicesApi';
import * as Yup from 'yup';

const PixelConversionSelector = ({
    adsAccount,
    promotedObjectFields,
    promotedObjectData,
    onSelectedPixelId,
    onSelectedEventType,
    onBlur,
    isSaving,
    adSetFormik,
    objective = '',
    context = ''
}) => {

    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const { adsAccountId, adsAccountName } = adsAccount || {};

    const [selectedPixelId, setSelectedPixelId] = useState(promotedObjectData?.pixel_id);
    const [selectedEventType, setSelectedEventType] = useState(
        promotedObjectData?.custom_event_str ? promotedObjectData?.custom_event_str :
            promotedObjectData?.custom_conversion_id
                ? promotedObjectData.custom_conversion_id
                : promotedObjectData?.custom_event_type || ''
    );

    const [pixelIdError, setPixelIdError] = useState(true);
    const [eventTypeError, setEventTypeError] = useState(true);

    const { data: pixelList, isLoading: isPixelLoading, isError: pixelError } = useGetPixelsByAdaccountQuery(
        {
            adsAccountId,
        }, {}
    );

    useEffect(() => {
        if (pixelError) {

            toast({
                position: "top-right",
                title: "Error",
                description: pixelError?.data?.message,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    }, [pixelError]);


    const { data: CustomeventList, isLoading: isCustomListLoading, error: isCustomEventError } = useGetCustomConversionEventsQuery({
        adsAccountId,
    }, {});

    const { data: StatusList, isLoading: isStatusListLoading, error: isStatusError } = useGetCustomStatsQuery({
        pixelId: selectedPixelId,
        adsAccountId,
    }, { skip: !selectedPixelId });

    const [createPixel, { }] = useCreatePixelMutation();

    const eventTypeOptions = promotedObjectFields.custom_event_type?.options || [];

    // useEffect(() => {
    //     if (isCustomEventError) {
    //         toast({
    //             position: "top-right",
    //             title: "Error",
    //             description: isCustomEventError?.data?.message,
    //             status: "error",
    //             duration: 4000,
    //             isClosable: true,
    //         });
    //     }
    // }, [isCustomEventError]);

    useEffect(() => {
        if (isStatusError) {
            toast({
                position: "top-right",
                title: "Error",
                description: isStatusError?.data?.message,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    }, [isStatusError]);


    // This function filters and updates the eventTypeOptions
    const filterEventsByPixel = () => {
        if (selectedPixelId || CustomeventList?.length > 0 || StatusList?.length > 0) {
            const eventTypeFilteredOptions = eventTypeOptions?.map(eventType => ({
                ...eventType,
                category: 'Standard',
            }));

            const filteredEvents = (Array.isArray(CustomeventList) ? CustomeventList : [])
                .map(event => ({
                    ...event,
                    category: 'Custom',
                }))
                .filter(event => event?.pixel === selectedPixelId);

            const statusEvents = (Array.isArray(StatusList) ? StatusList : [])
                .map(status => ({
                    eventName: status.eventName,
                    label: status.label,
                    value: status.value,
                    available: status.available === undefined ? true : status.available,
                    category: 'Status',
                })) || [];

            const uniqueStatusEvents = statusEvents.filter(statusEvent =>
                !eventTypeOptions.some(option => option.eventName === statusEvent.eventName)
            );

            return [...uniqueStatusEvents, ...filteredEvents, ...eventTypeFilteredOptions];
        }
    };

    const handlePixelIdChange = (event) => {
        const selectedId = event.target.value;
        setPixelIdError(!selectedId);
        setSelectedPixelId(selectedId);

        const selectedOption = event.target.selectedOptions[0]; // get the selected option
        const selectedPixelOption = JSON.parse(selectedOption.getAttribute('data-option'));

        onSelectedPixelId(selectedPixelOption, adsAccountId);

        setSelectedEventType('');
        setEventTypeError(true);
    };
    const handleEventTypeChange = (event) => {
        const selectedType = event.target.value;
        setEventTypeError(!selectedType);
        setSelectedEventType(selectedType);

        const selectedOption = event.target.selectedOptions[0]; // get the selected option
        const selectedEventOption = JSON.parse(selectedOption.getAttribute('data-option')); // parse the stored eventOption object

        let optionToSend = {};
        if (selectedEventOption?.category === 'Custom') {
            optionToSend = {
                pixel_rule: selectedEventOption?.pixel_rule,
                custom_conversion_id: selectedEventOption?.custom_conversion_id,
                custom_event_type: selectedEventOption?.value,
            }
        } else if (selectedEventOption?.category === 'Status') {
            optionToSend = {
                custom_event_type: selectedEventOption?.eventName,
                custom_event_str: selectedEventOption?.value,
            }
        } else {
            optionToSend = {
                custom_event_type: selectedEventOption?.value,
            }
        }

        if (selectedEventOption) {
            onSelectedEventType(optionToSend, adsAccountId);
        } else {
            const customTypeEnd = {
                custom_event_type: selectedType,
            }
            onSelectedEventType(customTypeEnd, adsAccountId);
        }
    };

    const validationSchema = Yup.object({
        pixelName: Yup.string().required('Pixel name is required'),
    });

    const handleSubmit = async (values, actions) => {
        const payload = { 'name': values.pixelName };

        await createPixel({ adsAccountId, payload }).unwrap()
            .then((response) => {
                if (response?.data) {
                    toast({
                        position: "top-right",
                        title: "Saved",
                        description: 'Pixel created',
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                }
            })
            .catch((error) => {
                if (error) (
                    toast({
                        position: "top-right",
                        title: "Error",
                        description: error?.message,
                        status: "error",
                        duration: 8000,
                        isClosable: true,
                    })
                );
            });
        actions.resetForm();
        onClose();
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Create New Pixel</ModalHeader>
                    <ModalCloseButton />
                    <Formik
                        initialValues={{ pixelName: '' }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ errors, touched }) => (
                            <Form>
                                <ModalBody>
                                    <FormControl id="pixel-name" isInvalid={errors.pixelName && touched.pixelName} isRequired>
                                        <FormLabel>Pixel Name</FormLabel>
                                        <Field
                                            name="pixelName"
                                            as={Input}
                                            placeholder="Enter pixel name"
                                        />
                                        {errors.pixelName && touched.pixelName ? (
                                            <div style={{ color: 'red.500', marginTop: '0.25rem' }}>{errors.pixelName}</div>
                                        ) : null}
                                    </FormControl>
                                </ModalBody>

                                <ModalFooter>
                                    <Button colorScheme="brand" mr={3} type="submit" size={'sm'}>
                                        Save
                                    </Button>
                                    <Button variant="ghost" onClick={onClose} size={'sm'}>
                                        Cancel
                                    </Button>
                                </ModalFooter>
                            </Form>
                        )}
                    </Formik>
                </ModalContent>
            </Modal>

            <Box mb="10px">
                {context === 'single' ? null :
                    <Flex gap={2} alignItems="center" mb={1}>
                        <Text fontWeight="bold" fontSize="12px">{adsAccountName}</Text>
                        <Text fontSize="12px">({adsAccountId})</Text>
                    </Flex>
                }

                {isPixelLoading ? (
                    <Flex>
                    <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
                    </Flex>
                ) : pixelList?.length > 0 ? (
                    <Flex
                    gap="20px"
                    direction={{ base: "column", md: "row" }} 
                    >
                    <Box flex={{ base: "unset", md: 1 }}>
                        <Flex alignItems="center" mb="5px">
                        <Text htmlFor="pixel-id" fontWeight="bold">Dataset</Text>
                        <Text display="inline-flex" fontSize="18px" color="red.400" mr={2}>*</Text>
                        <Tooltip
                            borderRadius={5}
                            label={
                            <Flex fontSize="sm" p="5px" gap={2} direction="column">
                                <Text fontWeight="bold">About datasets</Text>
                                <Text>
                                A dataset (formerly pixel) is a virtual container for the event data you send from sources like your website, app, or physical store, using the Meta Pixel, Conversions API, and other tools.
                                </Text>
                                <Text fontWeight="bold">Can’t find a dataset ID?</Text>
                                <Text>
                                Only dataset IDs associated with this ad account are shown here. If you can’t find a dataset ID, it may not be associated with this account. Go to Meta Business Manager to share the dataset with this account.
                                </Text>
                            </Flex>
                            }
                            fontSize="md"
                        >
                            <InfoOutlineIcon pointerEvents="all" />
                        </Tooltip>
                        </Flex>
                        <Select
                        id="pixel-id"
                        value={selectedPixelId}
                        placeholder="Select Pixel ID"
                        onChange={handlePixelIdChange}
                        size="sm"
                        borderRadius="md"
                        onBlur={onBlur}
                        >
                        {pixelList?.map((pixel) => (
                            <option key={pixel.id} value={pixel.id} data-option={JSON.stringify(pixel)}>
                            {pixel.name}
                            </option>
                        ))}
                        </Select>

                        {pixelIdError && isSaving && !selectedPixelId && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                            Pixel ID is required.
                        </Text>
                        )}
                    </Box>

                    <Box flex={{ base: "unset", md: 1 }}>
                        <Flex alignItems="center" mb="5px">
                        <Text htmlFor="event-type" fontWeight="bold">Conversion event</Text>
                        <Text display="inline-flex" fontSize="18px" color="red.400" mr={2}>*</Text>
                        <Tooltip
                            borderRadius={5}
                            label={
                            <Box fontSize="sm" p="5px">
                                <Text>
                                Choose a conversion event associated with your Meta pixel. A conversion event is an action that you want People to take when they see your ads.
                                </Text>
                            </Box>
                            }
                            fontSize="md"
                        >
                            <InfoOutlineIcon pointerEvents="all" />
                        </Tooltip>
                        </Flex>

                        {/* {isCustomListLoading || isStatusListLoading ? ( */}
                        {isStatusListLoading ? (
                        <Flex justifyContent="center" alignItems="center" mt={2}>
                            <SkeletonText width="100%" noOfLines={2} skeletonHeight={2} />
                        </Flex>
                        ) : (
                        <Select
                            id="event-type"
                            value={selectedEventType}
                            onChange={handleEventTypeChange}
                            size="sm"
                            isDisabled={!selectedPixelId}
                            borderRadius="md"
                        >
                            <option value="" disabled>Select Event Type</option>
                            {(adSetFormik?.values?.optimization_goal === 'VALUE' || (adSetFormik.values?.destination_type === 'WEBSITE_AND_PHONE_CALL' && objective === 'OUTCOME_SALES')) ? (
                            <option value="PURCHASE">Purchase</option>
                            ) : (['WEBSITE_AND_PHONE_CALL', 'WEBSITE_AND_LEAD_FORM'].includes(adSetFormik.values?.destination_type) && objective === 'OUTCOME_LEADS') ? (
                            <option value="LEAD">Lead</option>
                            ) : filterEventsByPixel()?.map((eventOption, index) => (
                            <option
                                key={`${eventOption?.value}-${index}`}
                                value={eventOption?.category === 'Custom' ? eventOption?.custom_conversion_id : eventOption?.value}
                                disabled={!eventOption?.available}
                                data-option={JSON.stringify(eventOption)}
                            >
                                {eventOption?.label}
                            </option>
                            ))}
                        </Select>
                        )}

                        {eventTypeError && isSaving && (!selectedEventType || selectedEventType === '') && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                            Event type is required.
                        </Text>
                        )}
                    </Box>
                    </Flex>
                ) : (
                    <Flex gap="10px">
                    <InfoOutlineIcon pointerEvents="all" color="red.500" />
                    <Text fontSize="12px" color="red.500">
                        The Meta pixel is required. To publish, set up your pixel or switch to a different optimisation, such as landing page views
                    </Text>
                    </Flex>
                )}
            </Box>
        </>
    );
};

export default PixelConversionSelector;
